import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import menuPortalProps from 'helpers/reactSelectPortal';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { isAfter } from 'date-fns';
import { toast } from 'react-toastify';
import {
  FaBan, FaComment, FaDownload, FaGg, FaInfoCircle, FaMapMarkerAlt,
  FaMoneyBill, FaBuilding, FaPlus, FaProjectDiagram, FaSave, FaPlaneDeparture,
} from 'react-icons/fa';
import apiAxios from 'helpers/apiAxios';
import { createBooking, updateBooking } from 'redux/booking/travel/travelBookingSlice';
import { getCountries, getExchangeRate } from 'redux/settingsSlice';
import { getProfileUser } from 'redux/profileUserSlice';
import { getActiveSites } from 'redux/organization/siteSlice';
import { debounce, makeEmployeeLoader, makeEmployeeIdLoader } from 'helpers/debounce';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import GoogleMapAutoCompletePlaceDebounce from 'components/common/google-map-places/GoogleMapAutoCompletePlaceDebounce';
import { TRAVEL_ARRANGEMENT_BY_HR_ADMIN, TRAVEL_TYPE_OF_TRAVEL, TRAVEL_PURPOSE_OF_TRAVEL } from 'app_config';
import { Accordion, Button, Alert, FormRow, CurrencySelect, ToggleSwitch } from 'components/common';
import { FormHero } from 'components/app/bookings/common/FormLayout';
import BookingMultipleStopTripForm from './BookingMultipleStopTripForm';


import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const hint = 'tw-mt-1.5 tw-text-xs tw-text-slate-500 tw-flex tw-items-center tw-gap-1';

// Accordion section header: blue icon chip + title, for a consistent accent.
const accHeader = (Icon, text) => (
  <span className="tw-flex tw-items-center tw-gap-2.5">
    <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-bg-blue-100 tw-text-blue-600 tw-flex-shrink-0"><Icon size={13} /></span>
    <span className="tw-font-semibold tw-text-slate-700 tw-text-sm">{text}</span>
  </span>
);

const BookingForm = ({ title, booking }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, reset, setError, clearErrors, watch } = useForm();
  const { usdExchangeRateData, usdExchangeRateDate, countryData } = useSelector((state) => state.settings);
  const { data: profileUser } = useSelector((state) => state.profile_user);

  const [customersTravelerTags, setCustomersTravelerTags] = useState([]);
  const [activeKeys, setActiveKeys] = useState(['0', '1', '2', '3', '4', '5']);
  const [selectedArrangements, setSelectedArrangements] = useState(booking?.arrangement_by_hr_admin ?? []);
  const [transitBookings, setTransitBookings] = useState([]);
  const [transitsToDelete, setTransitsToDelete] = useState([]);
  const [address, setAddress] = useState(null);
  const [showTripForProjectTab, setShowTripForProjectTab] = useState((booking && booking.purpose_of_travel === TRAVEL_PURPOSE_OF_TRAVEL[2]) || false);

  const { activeData: siteData } = useSelector((state) => state.organization_site);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingUrl = '/booking/travel';
  const { site: { currency: SITE_DEFAULT_CURRENCY = 'VND', country: SITE_DEFAULT_COUNTRY = 'VN' } = {} } = profileUser || {};

  useEffect(() => { if (title === 'New travel request') reset(); }, [title, reset]);

  const fetchRef = useRef({ countries: false, sites: false, profile: false });

  useEffect(() => {
    if ((!siteData || siteData.length === 0) && !fetchRef.current.sites) {
      fetchRef.current.sites = true;
      dispatch(getActiveSites()).finally(() => { fetchRef.current.sites = false; });
    }
    if ((!countryData || countryData.length === 0) && !fetchRef.current.countries) {
      fetchRef.current.countries = true;
      dispatch(getCountries()).finally(() => { fetchRef.current.countries = false; });
    }
    const today = new Date().toISOString().slice(0, 10);
    if (!usdExchangeRateData || usdExchangeRateDate !== today) dispatch(getExchangeRate());
    if ((!profileUser || Object.keys(profileUser).length === 0) && !fetchRef.current.profile) {
      fetchRef.current.profile = true;
      dispatch(getProfileUser()).finally(() => { fetchRef.current.profile = false; });
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue('date_of_travel', new Date());
    setValue('country_code', SITE_DEFAULT_COUNTRY);
    setValue('currency', SITE_DEFAULT_CURRENCY);
    setValue('site_id', profileUser.site?.id);
    setValue('purpose_of_travel', TRAVEL_PURPOSE_OF_TRAVEL[4]);

    if (booking) {
      Object.entries(booking).forEach(([key, value]) => {
        if (['date_of_travel', 'date_of_return', 'expected_approval'].includes(key)) {
          if (value) setValue(key, new Date(value));
        } else if (key === 'customers_traveler') {
          if (value) setCustomersTravelerTags(value.map((item) => ({ value: item, label: item })));
        } else if (key === 'employees') {
          setValue('employee_ids', value && value.map((emp) => emp.employee_id));
        } else if (key === 'destination') {
          if (value) setValue('destination', value);
        } else setValue(key, value);
      });
      setTransitBookings(booking.transits);
    }
  }, [setValue, booking]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (usdExchangeRateData && !booking) setValue('exchange_rate', usdExchangeRateData); }, [usdExchangeRateData, booking, setValue]);
  useEffect(() => { setValue('customers_traveler', customersTravelerTags.map((opt) => opt.value)); }, [customersTravelerTags, setValue]);

  useEffect(() => {
    if (address !== null) {
      if (address?.country) setValue('country_code', address.country);
      if (address?.description) { setValue('destination', address.description); clearErrors('destination'); }
    }
  }, [address, setValue, clearErrors]);

  const validateJobNumber = () => {
    const job_number = getValues('job_number');
    if ((job_number === undefined || (job_number !== undefined && job_number?.includes('Q'))) && getValues('follow_project_budget')) {
      setError('job_number', { type: 'manual', message: 'Job number must be required' });
      return false;
    }
    clearErrors('job_number');
    return true;
  };

  const validateTraveller = () => {
    // Treat an empty array like "not set": picking travellers and then removing
    // them all leaves [], which the previous `=== undefined` check let through —
    // the trip could be submitted with nobody on it.
    const isEmpty = (v) => !v || v.length === 0;

    if (isEmpty(getValues('customers_traveler')) && isEmpty(getValues('employee_ids'))) {
      setError('employee_ids', { type: 'manual', message: 'Travelers were empty. You must specify employees or customers travel for the trip.' });
      return false;
    }
    clearErrors('employee_ids');
    return true;
  };

  const validateArrangeByHrAdmin = () => {
    const arrangement = getValues('arrangement_by_hr_admin');
    if (arrangement && arrangement.length === 0) {
      setError('arrangement_by_hr_admin', { type: 'manual', message: 'Arrangement by HR Admin is empty. You must specify the required arrangements by HR admin.' });
      return false;
    }
    clearErrors('arrangement_by_hr_admin');
    return true;
  };

  const validateDestination = () => {
    const formDestination = getValues('destination');
    const bookingDestination = booking?.destination;
    const addressDesc = address?.description;

    if (booking) {
      if (!address) {
        if (formDestination || bookingDestination) { clearErrors('destination'); return true; }
        const message = 'Destination is required. Please select from the dropdown suggestions.';
        setError('destination', { type: 'manual', message }); toast.error(message); return false;
      }
      if (address && !addressDesc) {
        const message = 'Please select a valid destination from the dropdown suggestions.';
        setError('destination', { type: 'manual', message }); toast.error(message); return false;
      }
      clearErrors('destination');
      return true;
    }

    if (!addressDesc && !formDestination) {
      const message = 'Please select a valid destination from the dropdown suggestions.';
      setError('destination', { type: 'manual', message }); toast.error(message); return false;
    }
    clearErrors('destination');
    return true;
  };

  const onSubmit = async (data, event) => {
    if (!validateTraveller() || !validateJobNumber() || !validateArrangeByHrAdmin() || !validateDestination()) return;

    const saveAndSendRequest = event.nativeEvent.submitter.value === 'saveAndSendRequest';

    let payload = data;
    if (transitBookings) {
      if (transitsToDelete) {
        const deletedTrans = transitsToDelete.map((item) => ({ ...item, _destroy: '1' }));
        payload = { ...payload, transits_attributes: [...transitBookings, ...deletedTrans] };
      } else {
        payload = { ...payload, transits_attributes: transitBookings };
      }
    }
    payload = { ...payload, destination: address?.description || booking?.destination, save_and_send_request: saveAndSendRequest };

    try {
      let bookingId = '';
      if (booking) {
        bookingId = booking.id;
        await dispatch(updateBooking({ bookingId, data: payload })).unwrap();
      } else {
        const responseData = await dispatch(createBooking(payload)).unwrap();
        bookingId = responseData?.data?.id;
      }
      if (bookingId) navigate(`${bookingUrl}/${bookingId}`);
    } catch (error) {
      console.error('Booking submission error:', error);
    }
  };

  const siteOptions = useMemo(() => siteData && siteData.map((site) => ({ value: site?.id, label: site?.name })), []); // eslint-disable-line react-hooks/exhaustive-deps
  const defaultSiteOption = useMemo(() => (booking ? { value: booking?.site_id, label: booking?.site_name } : { value: profileUser.site?.id, label: profileUser.site?.name }), [booking]); // eslint-disable-line react-hooks/exhaustive-deps
  const countryOptions = useMemo(() => (countryData || []).map((option) => ({ value: option.country, label: option.name })), [countryData]);
  const currencyOptions = useMemo(() => [{ value: 'VND', label: 'VND' }, { value: 'USD', label: 'USD' }, { value: 'THB', label: 'THB' }], []);

  // One debounced loader per select. The module-level debounced helpers used
  // before were shared across every mounted picker, so typing in one field could
  // cancel the other's in-flight request.
  const employeeOptions = useMemo(() => makeEmployeeIdLoader(), []);
  const defaultEmployeeOption = useMemo(
    () => (booking?.employees
      ? booking.employees.map((emp) => ({
          value: emp.employee_id,
          label: emp.employee_name,
          fullName: emp.employee_name,
          email: emp.employee_email,
        }))
      : null),
    [booking],
  );

  // cc_emails_optional stores employee NAMES, not addresses — the backend resolves
  // them with by_user_names(...).pluck(:email) when the trip is approved. Hence the
  // name-valued loader here.
  const ccNameOptions = useMemo(() => makeEmployeeLoader(), []);
  const defaultCCnameOption = useMemo(
    () => (booking?.cc_emails_optional ? booking.cc_emails_optional.map((name) => ({ value: name, label: name })) : null),
    [booking],
  );

  const handleBack = () => navigate(-1);

  // Customers traveler: creatable multi-select. Loads the linked customer's
  // contacts (from the selected job) for quick picking, but still accepts freely
  // typed names. Stored as a plain string[] in `customers_traveler` (BE contract).
  const debouncedTravelerContacts = debounce((inputValue, callback) => {
    const customerId = getValues('customer_id');
    if (!customerId) { callback([]); return; }
    apiAxios.get(`/crm/companies/${customerId}/contacts?q=${encodeURIComponent(inputValue || '')}`)
      .then((res) => {
        const rows = res.data?.data || res.data || [];
        const opts = rows.map((row) => {
          const a = row.attributes || row;
          const name = a.full_of_name || a.full_name || a.name;
          return name ? { value: name, label: name, email: a.email } : null;
        }).filter(Boolean);
        callback(opts);
      })
      .catch(() => callback([]));
  }, 300);
  const travelerContactOptions = (inputValue, callback) => debouncedTravelerContacts(inputValue, callback);
  const formatTravelerOption = (opt) => (
    <span>{opt.label}{opt.email ? <small style={{ color: '#94a3b8', marginLeft: 6 }}>{opt.email}</small> : null}</span>
  );

  const travelBookingUrlBE = '/booking/travel/bookings';
  const filterJobNumberOptions = async (inputValue) => {
    try {
      const url = `${travelBookingUrlBE}/list_quote_jobs?page=1&per_page=30&q=${encodeURIComponent(inputValue)}`;
      const response = await apiAxios.get(url);
      const { data } = response;
      const isFollowProjectBudget = watch('follow_project_budget') || false;
      if (!data) return [];
      return data.map((item) => {
        const number = isFollowProjectBudget ? item.j_number : item.q_number;
        return {
          value: number,
          label: `${number}-${item.name}`,
          project_id: item.id,
          project_name: item.name,
          customer_id: item.company_id,
          customer_name: item.company_name,
        };
      }).filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
    } catch (error) {
      console.error(error);
      return [];
    }
  };
  const debouncedFilterJobNumbers = debounce((inputValue, callback) => filterJobNumberOptions(inputValue).then(callback), 300);
  const jobNumberOptions = (inputValue, callback) => debouncedFilterJobNumbers(inputValue, callback);
  const defaultJobNumberOption = useMemo(() => (booking && booking.project ? { value: booking.project.job_number, label: booking.project.job_number } : null), [booking]);

  // Job number is the SINGLE driver: picking a job auto-fills Customer + Project
  // (shown read-only) so the three can never be saved mutually inconsistent
  // (the backend derives project_id/customer_id from job_number anyway).
  const [jobMeta, setJobMeta] = useState(() => (
    booking?.project
      ? { customer_name: booking.project.customer_name, project_name: booking.project.project_name }
      : null
  ));

  const handleSelectJob = (opt) => {
    if (opt) {
      setValue('job_number', opt.value);
      setValue('customer_id', opt.customer_id ?? null);
      setValue('project_id', opt.project_id ?? null);
      setJobMeta({ customer_name: opt.customer_name, project_name: opt.project_name });
      clearErrors('job_number');
    } else {
      setValue('job_number', undefined);
      setValue('customer_id', null);
      setValue('project_id', null);
      setJobMeta(null);
    }
  };

  useEffect(() => setValue('arrangement_by_hr_admin', selectedArrangements, { shouldValidate: true }), [selectedArrangements, setValue]);

  const handleCheckboxArrangementChange = (option) => {
    setSelectedArrangements((prev) => (prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]));
  };

  const handleAddTransit = useCallback(() => {
    const date_of_travel = getValues('date_of_travel');
    const date_of_return = getValues('date_of_return');
    const newTransit = {
      from_date: date_of_travel ? new Date(date_of_travel) : new Date(),
      to_date: date_of_return ? new Date(date_of_return) : new Date(),
      from_location: null, to_location: null, description: null, user_id: profileUser.id,
    };
    setTransitBookings([...transitBookings, newTransit]);
  }, [transitBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveTransit = (index) => {
    setTransitBookings(transitBookings.filter((item, i) => i !== index));
    setTransitsToDelete(transitBookings.filter((item, i) => i === index));
  };

  const handleUpdatedTransit = (index, transit) => {
    const updated = [...transitBookings];
    updated[index] = transit;
    setTransitBookings(updated);
  };

  const validateReturnDate = (value) => {
    const fromDate = getValues('date_of_travel');
    const parsedFrom = fromDate ? new Date(fromDate) : null;
    const parsedTo = value ? new Date(value) : null;
    return (parsedFrom && parsedTo && isAfter(parsedTo, parsedFrom)) || 'Date of return must be after date of travel.';
  };

  const approvedStatus = booking && booking.status.id === 3;


  return (
    <div className="tw-p-4">
      <div className="tw-max-w-4xl tw-mx-auto">
        <FormHero
          icon={FaPlaneDeparture}
          title={title}
          subtitle="Applies to overnight business trips · approved Vietnam trips sync to iHRP automatically"
          onBack={handleBack}
        />
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-mt-3">
          <form onSubmit={handleSubmit(onSubmit)} className="tw-p-4">
            <Alert type="warning" className="tw-mb-4">
              This request applies only to business trips that last overnight. For employees based in Vietnam, once a trip is approved, it will be automatically synchronized with iHRP.
            </Alert>

            <Accordion activeKeys={activeKeys} onChange={setActiveKeys}>
            {/* Trip information */}
            <Accordion.Item eventKey="0" header={accHeader(FaInfoCircle, 'Trip information')}>
              <div className="tw-p-4 tw-max-w-3xl">
                <FormRow label="Trip name" required error={errors.trip_name?.message}>
                  <input placeholder="Trip name" className={inputClass} {...register('trip_name', { required: 'Trip name must be required.' })} />
                </FormRow>

                <FormRow label="Destination" required error={errors.destination?.message}>
                  <GoogleMapAutoCompletePlaceDebounce id="travel-destination" setAddress={setAddress} defaultAddress={booking?.destination} isRequired />
                </FormRow>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
                  <FormRow label="Date of travel" required error={errors.date_of_travel?.message}>
                    <Controller control={control} name="date_of_travel" rules={{ required: 'Date of travel must be required.' }}
                      render={({ field }) => (
                        <DatePicker selected={field.value} className={inputClass} placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight
                          onChange={(date) => { field.onChange(date); if (!getValues('date_of_return')) setValue('date_of_return', date); }} />
                      )} />
                  </FormRow>

                  <FormRow label="Date of return" required error={errors.date_of_return?.message}>
                    <Controller control={control} name="date_of_return" rules={{ required: 'Date of return must be required.', validate: { afterFromDate: (v) => validateReturnDate(v) } }}
                      render={({ field }) => (
                        <DatePicker selected={field.value} onChange={(d) => field.onChange(d)} className={inputClass} placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight />
                      )} />
                  </FormRow>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4">
                  <FormRow label="Type of trip" required>
                    <Controller control={control} name="type_of_travel" defaultValue={booking?.type_of_travel || TRAVEL_TYPE_OF_TRAVEL[0]}
                      render={({ field }) => (
                        <div className="tw-flex tw-gap-3 tw-mt-1">
                          {TRAVEL_TYPE_OF_TRAVEL.map((option) => {
                            const selected = field.value === option;
                            return (
                              <label key={option}
                                className={`tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-px-3 tw-py-2 tw-rounded tw-border tw-border-solid tw-text-sm tw-transition-colors ${selected ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700 tw-font-medium' : 'tw-border-slate-200 tw-text-slate-600 hover:tw-border-blue-300'}`}>
                                <input type="radio" value={option} checked={selected}
                                  onChange={() => { field.onChange(option); setValue('currency', option === 'International' ? 'USD' : SITE_DEFAULT_CURRENCY); }} />
                                {option}
                              </label>
                            );
                          })}
                        </div>
                      )} />
                  </FormRow>

                  <FormRow label="Trip to country" required error={errors.country_code?.message}>
                    <Controller control={control} name="country_code" rules={{ required: { value: true, message: 'Trip to country is required' } }}
                      render={({ field }) => (
                        <Select {...field} options={countryOptions} value={countryOptions.find((o) => o.value === field.value)} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                          onChange={(opt) => { field.onChange(opt.value); setValue('country_code', opt.value, { shouldValidate: true }); }} />
                      )} />
                  </FormRow>
                </div>

                <FormRow label="From site office" required error={errors.site_id?.message}>
                  <Controller control={control} name="site_id" rules={{ required: { value: true, message: 'Site must be required' } }}
                    render={() => (
                      <Select closeMenuOnSelect options={siteOptions} defaultValue={defaultSiteOption} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                        formatOptionLabel={(o) => <span className="tw-flex tw-items-center tw-gap-2"><FaBuilding className="tw-text-slate-400" size={12} />{o.label}</span>}
                        onChange={(opt) => setValue('site_id', opt.value, { shouldValidate: true })} />
                    )} />
                  <div className={hint}><FaInfoCircle /> Specify the correct site office, as the booking request will be forwarded to the HR Admin of that office.</div>
                </FormRow>

                <FormRow label="Purpose of travel" required error={errors.purpose_of_travel?.message}>
                  <Controller control={control} name="purpose_of_travel" rules={{ required: 'Purpose of travel must be required.' }} defaultValue={booking?.purpose_of_travel || TRAVEL_PURPOSE_OF_TRAVEL[3]}
                    render={({ field }) => (
                      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2 tw-mt-1">
                        {TRAVEL_PURPOSE_OF_TRAVEL.map((option) => (
                          <label key={option} className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                            <input type="radio" value={option} checked={field.value === option}
                              onChange={() => { field.onChange(option); setShowTripForProjectTab(option === TRAVEL_PURPOSE_OF_TRAVEL[2]); }} />
                            <span className="tw-text-sm tw-text-slate-600">{option}</span>
                          </label>
                        ))}
                      </div>
                    )} />
                </FormRow>

                <FormRow label="Description" required error={errors.description_purpose_of_travel?.message}>
                  <textarea rows={5} placeholder="Description purpose of travel" className={inputClass} {...register('description_purpose_of_travel', { required: 'Description purpose of travel must be required.' })} />
                </FormRow>

                {/* FormRow already renders the label, so EmployeeSelectField is
                    given none — otherwise the field would show two. */}
                <FormRow label="Employees traveler" required error={errors.employee_ids?.message}>
                  <EmployeeSelectField
                    name="employee_ids"
                    control={control}
                    loadOptions={employeeOptions}
                    defaultValue={defaultEmployeeOption}
                    placeholder="Search by employee code or name…"
                    isMulti
                  />
                </FormRow>

                <FormRow label="Arrangement by HR Admin" required error={errors.arrangement_by_hr_admin?.message}>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2 tw-mt-1">
                    {TRAVEL_ARRANGEMENT_BY_HR_ADMIN.map((option) => (
                      <label key={option} className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm">
                        <input type="checkbox" checked={selectedArrangements.includes(option)} onChange={() => handleCheckboxArrangementChange(option)} />
                        <span className="tw-text-slate-600">{option}</span>
                      </label>
                    ))}
                  </div>
                </FormRow>

                <FormRow label="CC emails (optional)" error={errors.cc_emails_optional?.message}>
                  <EmployeeSelectField
                    name="cc_emails_optional"
                    control={control}
                    loadOptions={ccNameOptions}
                    defaultValue={defaultCCnameOption}
                    placeholder="Search colleagues to keep in the loop…"
                    isMulti
                  />
                  <div className={hint}><FaInfoCircle /> CC others for email tracking of the trip upon approval.</div>
                </FormRow>
              </div>
            </Accordion.Item>

            {/* Multi stop trips */}
            <Accordion.Item eventKey="1" header={accHeader(FaMapMarkerAlt, 'Multi stop trips')}>
              <div className="tw-p-4">
                <Alert type="info" className="tw-mb-3">For booking multi-stop trips, please provide details including the starting date, ending date, and locations.</Alert>
                <div className="tw-flex tw-justify-end tw-mb-3">
                  <Button size="sm" variant="secondary" leftIcon={FaPlus} onClick={handleAddTransit}>Add stop trip</Button>
                </div>
                {transitBookings.map((item, index) => (
                  <BookingMultipleStopTripForm
                    index={index}
                    key={index}
                    transit={item}
                    control={control}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                    errors={errors}
                    removeTransit={() => handleRemoveTransit(index)}
                    updatedTransit={handleUpdatedTransit}
                  />
                ))}
              </div>
            </Accordion.Item>

            {/* Trip for project */}
            {showTripForProjectTab && (
              <Accordion.Item eventKey="2" header={accHeader(FaProjectDiagram, 'Trip for project')}>
                <div className="tw-p-4 tw-max-w-3xl">
                  <FormRow label="">
                    <Controller control={control} name="follow_project_budget"
                      render={({ field }) => (
                        <ToggleSwitch
                          value={!!field.value}
                          label="Follow project budget"
                          onChange={(checked) => { setValue('follow_project_budget', checked); if (checked) validateJobNumber(); else clearErrors('job_number'); }}
                        />
                      )} />
                  </FormRow>
                  <FormRow label="Quote/Job number" error={errors.job_number?.message}>
                    <Controller control={control} name="job_number"
                      render={() => (
                        <AsyncSelect cacheOptions defaultOptions loadOptions={jobNumberOptions} defaultValue={defaultJobNumberOption} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps} isClearable isDisabled={approvedStatus}
                          onChange={handleSelectJob} />
                      )} />
                    <div className={hint}>
                      {approvedStatus
                        ? <span className="tw-text-red-600"><FaInfoCircle /> Once the trip is approved, the job number cannot be changed.</span>
                        : <><FaInfoCircle /> Selecting a job auto-fills the customer &amp; project below.</>}
                    </div>
                  </FormRow>
                  <FormRow label="Customer">
                    <div className="tw-px-3 tw-py-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700">
                      {jobMeta?.customer_name || <span className="tw-text-slate-400">— derived from job —</span>}
                    </div>
                  </FormRow>
                  <FormRow label="Project">
                    <div className="tw-px-3 tw-py-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700">
                      {jobMeta?.project_name || <span className="tw-text-slate-400">— derived from job —</span>}
                    </div>
                  </FormRow>
                  <FormRow label="Customers traveler" error={errors.customers_traveler?.message}>
                    <AsyncCreatableSelect
                      isMulti
                      cacheOptions
                      defaultOptions
                      value={customersTravelerTags}
                      loadOptions={travelerContactOptions}
                      formatOptionLabel={formatTravelerOption}
                      onChange={(opts) => setCustomersTravelerTags(opts || [])}
                      placeholder="Select from customer contacts or type a name…"
                      classNamePrefix="react-select"
                      {...menuPortalProps}
                      noOptionsMessage={() => (getValues('customer_id') ? 'No contacts found — type to add' : 'Type a name to add')}
                      formatCreateLabel={(input) => `Add "${input}"`}
                    />
                    <div className={hint}><FaInfoCircle /> Pick from the customer&apos;s contacts, or type a name and press enter to add.</div>
                  </FormRow>
                </div>
              </Accordion.Item>
            )}

            {/* Cash advance */}
            <Accordion.Item eventKey="3" header={accHeader(FaMoneyBill, 'Cash Advance')}>
              <div className="tw-p-4 tw-max-w-3xl">
                <FormRow label="Cash Advance">
                  <input placeholder="Cash Advance" className={inputClass} {...register('advance_cost_currency')} />
                  <div className={hint}><FaInfoCircle /> The currency for the cash advance will match that of the trip. Click <FaDownload /> Advance Payment Request template.</div>
                </FormRow>
              </div>
            </Accordion.Item>

            {/* Currency */}
            <Accordion.Item eventKey="4" header={accHeader(FaGg, 'Currency of trip')}>
              <div className="tw-p-4 tw-max-w-3xl">
                <FormRow label="Currency" required error={errors.currency?.message}>
                  <CurrencySelect value={watch('currency')} options={currencyOptions.map((o) => o.value)} onChange={(v) => setValue('currency', v, { shouldValidate: true })} />
                </FormRow>
                <FormRow label="Exchange rate" error={errors.exchange_rate?.message}>
                  <input type="text" placeholder="Exchange rate" className={inputClass} {...register('exchange_rate', { required: 'Exchange rate must be required' })} />
                  <Alert type="info" className="tw-mt-2">USD exchange rate to VND from <a href="https://www.vietcombank.com.vn/KHCN/Cong-cu-tien-ich/Ty-gia?devicechannel=default" target="_blank" rel="noreferrer" className="tw-underline">Vietcombank</a>.</Alert>
                </FormRow>
              </div>
            </Accordion.Item>

            {/* Comment */}
            <Accordion.Item eventKey="5" header={accHeader(FaComment, 'Comment')}>
              <div className="tw-p-4 tw-max-w-3xl">
                <FormRow label="Comment for booking">
                  <textarea rows={10} placeholder="Comment for booking" className={inputClass} {...register('comment_for_booking')} />
                </FormRow>
              </div>
            </Accordion.Item>
            </Accordion>

            <div className="-tw-mx-4 -tw-mb-4 tw-mt-6 tw-px-4 tw-py-3.5 tw-bg-slate-50 tw-border-t tw-border-solid tw-border-slate-200 tw-rounded-b-lg tw-flex tw-justify-center tw-gap-3">
              <Button size="sm" type="submit" name="action" value="saveAndSendRequest" variant="primary" leftIcon={FaSave}>{booking ? 'Update' : 'Save and send travel request'}</Button>
              {!booking && <Button size="sm" type="submit" name="action" value="saveDraf" variant="secondary" leftIcon={FaSave}>Save Draft</Button>}
              <Button size="sm" variant="secondary" leftIcon={FaBan} onClick={handleBack}>Cancel</Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

BookingForm.propTypes = {
  title: PropTypes.string.isRequired,
  booking: PropTypes.object,
};

export default BookingForm;
